
import { useState } from 'react';
import { Bell, CheckCircle, AlertTriangle, Info, AlertCircle } from 'lucide-react';
import { Notification, NotificationType } from '../types';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { formatDistanceToNow } from 'date-fns';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: (id: string) => void;
}

export function NotificationItem({ notification, onMarkAsRead }: NotificationItemProps) {
  const [isHovered, setIsHovered] = useState(false);

  const getIcon = (type: NotificationType) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-amber-500" />;
      case 'alert':
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      case 'info':
      default:
        return <Info className="h-5 w-5 text-blue-500" />;
    }
  };

  const handleClick = () => {
    if (!notification.read) {
      onMarkAsRead(notification.id);
    }
  };

  const cardContent = (
    <CardContent className="p-4 flex items-start gap-4">
      <div className="mt-1">{getIcon(notification.type)}</div>
      <div className="flex-1 space-y-1">
        <div className="flex items-center justify-between">
          <h4 className={cn("font-medium", !notification.read && "font-semibold")}>
            {notification.title}
          </h4>
          <span className="text-xs text-muted-foreground">
            {formatDistanceToNow(new Date(notification.timestamp), { addSuffix: true })}
          </span>
        </div>
        <p className="text-sm text-muted-foreground">{notification.message}</p>
      </div>
      {!notification.read && isHovered && (
        <Button 
          variant="ghost" 
          size="sm" 
          className="ml-auto" 
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onMarkAsRead(notification.id);
          }}
        >
          Mark as read
        </Button>
      )}
    </CardContent>
  );

  return (
    <Card 
      className={cn(
        "transition-all hover:shadow-md cursor-pointer",
        !notification.read && "bg-muted/50 border-l-4 border-l-primary"
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleClick}
    >
      {notification.link ? (
        <Link to={notification.link}>{cardContent}</Link>
      ) : (
        cardContent
      )}
    </Card>
  );
}
